import React, { useEffect, useMemo, useRef, useState } from 'react';

import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import { patchAdmissionInsurance } from '../../services/api/admissionApi';
import { getRunningBill } from '../../services/api/admissionChargesApi';
import {
  APPROVAL_ROUND_ERROR,
  APPROVAL_ROUNDS_MAX,
  isTerminal,
  isInDischargeFlow,
  isMoneyHidden,
} from '../../constants/admission';
import { formatPaise, formatServiceDate, todayYmd } from '../../utils/money';
import { formatDateTimeIST } from '../../utils/formatIST';
import {
  deriveApprovalRounds,
  buildApprovalRoundsPatchBody,
  sumApprovalRoundsPaise,
} from './insuranceModel';

const addBtnCls = 'text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60';
const formCls = 'border-2 border-dashed border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/40 rounded-lg p-3 mb-3';
const labelCls = 'block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1';
const inputCls = 'w-full px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]';
const formBtn = 'px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[12px] font-semibold hover:bg-slate-100 dark:hover:bg-slate-700';
const formBtnPrimary = 'px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12px] font-semibold hover:bg-teal-700 disabled:opacity-60';
const miniBtn = 'text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700';
const miniBtnDanger = 'text-[11px] font-semibold px-2 py-1 rounded-md border border-red-300 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30';
const rowCls = 'rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2.5';
const rowClsLegacy = 'rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-2.5';
const readonlyChip = 'text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300';
const errorCls = 'mt-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12px] p-2';
const warnCls = 'mt-2 rounded-lg border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-[12px] p-2';
const bannerCls = 'flex items-start gap-2 rounded-lg border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-[12px] p-2.5 mb-3';
const lockedNoteCls = 'text-[11.5px] italic text-slate-400 dark:text-slate-500 py-1';

function emptyDraft() {
  return { approved_on: todayYmd(), amount: '', reference: '', note: '', reason: '' };
}

function draftFromRound(round) {
  return {
    approved_on: round.approved_on || todayYmd(),
    amount: round.amount_paise != null ? String(round.amount_paise / 100) : '',
    reference: round.reference || '',
    note: round.note || '',
    reason: '',
  };
}

/**
 * ApprovalRoundsList — one row per TPA sanction letter on the Payer &
 * insurance card (TPA-PREAUTH/tpa-bill S2, FE-1). Binds VERBATIM to the
 * FROZEN C1 contract: `PATCH /admissions/{id}/insurance` with a FULL-LIST
 * `approval_rounds` replace + `expected_insurer_approved_paise` CAS. The
 * server computes `insurer_approved_paise` — this component never renders a
 * sum of its own (that stays in `PayerInsuranceCard`, reading the admission
 * row directly — single source of truth).
 *
 * The Add/Edit form is ONE inline form, always TOP-anchored above the rows
 * (never below — a long list scrolls inside `.rows`, the form does not move
 * with it) and scrolled into view when it opens. Delete goes through
 * `ConfirmationModal` — never a native `confirm()`.
 *
 * `canEdit` is the caller's combined gate (`COLLECT_DEPOSIT` permission AND
 * `canEditApprovalRounds(status)`) — this component renders read-only
 * (no Add/Edit/Remove controls at all, never a disabled-but-visible one)
 * when it is false.
 *
 * TPA-PREAUTH/tpa-bill S3 B-prime additions: rounds stay editable through
 * DISCHARGE_INITIATED/BILLING_CLEARANCE (`canEditApprovalRounds` widened by
 * the caller) — an amber banner explains the settlement-adjustment effect
 * while that window is open. Lowering the total below what's currently on
 * screen reveals a REQUIRED Reason field (cleared + hidden the moment the
 * amount is raised back); a projected total above the running bill's NET
 * CHARGES total (`total_charges_paise`, lazily read once when the form
 * opens) shows an inline warning but never blocks Save. Removing a round
 * inside the discharge flow asks for the same reason in the confirm modal.
 * Once terminal, the read-only view states the lock in one line.
 *
 * D1 fix / orchestrator ruling 2026-09-19 (legacy admissions — a non-zero
 * `insurer_approved_paise` predating this feature, `approval_rounds_legacy`):
 * the legacy virtual row is never DROPPED on the first write — it is carried
 * forward as an ordinary round ("history is not lost", the mockup's Legacy
 * admission tab) unless the desk explicitly removes it via its own Remove
 * control (no Edit control — its fields aren't directly tweakable). Because
 * it stays in the Σ by default, adding a first real round is almost always
 * an INCREASE (legacy + new); the only way to trigger the decrease/Reason
 * path on a legacy admission is to remove the legacy row itself.
 */
export default function ApprovalRoundsList({ admission, canEdit, onSaved }) {
  const admissionId = admission && admission.admission_id;
  const rounds = useMemo(() => deriveApprovalRounds(admission), [admission]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [billTotalPaise, setBillTotalPaise] = useState(null);
  const formRef = useRef(null);
  const billFetchedRef = useRef(false);

  useEffect(() => {
    if (formOpen) formRef.current?.scrollIntoView({ block: 'nearest' });
  }, [formOpen]);

  // ── INTEGRATION reconcile item (b): the over-approval warning compares the
  // projected approved total against the RUNNING BILL'S NET CHARGES TOTAL
  // (`RunningBillResponse.total_charges_paise` — the same field the running
  // bill's totals footer prints as "Total charges" and `WaiverConcessionDialog`
  // already receives as `netChargesPaise`), NOT `balance_paise` (which is net
  // of deposits/refunds/settlements and is therefore NOT the bill).
  //
  // The admission row carries no charges total, so this is the one place that
  // must read the running bill. LAZY + SINGLE-FLIGHT: nothing is fetched on
  // render or on mount — only the FIRST time the user opens the Add/Edit form
  // (a deliberate action), once per mounted component. Until it lands (or if
  // it fails) `billTotalPaise` stays null and NO warning is shown — a warning
  // is never fabricated off a fake ₹0 total.
  useEffect(() => {
    if (!formOpen || billFetchedRef.current || !admissionId) return;
    billFetchedRef.current = true;
    let alive = true;
    getRunningBill(admissionId).then((res) => {
      if (!alive || !res.success) return;
      setBillTotalPaise(Number(res.data?.total_charges_paise) || 0);
    });
    return () => { alive = false; };
  }, [formOpen, admissionId]);

  // ── S3 B-prime: live decrease + over-approval detection ────────────────
  // `currentTotalPaise` is the total as currently on screen (server value —
  // the legacy virtual row's amount already equals it, so this reproduces
  // `insurer_approved_paise` in both the legacy and real-rows case, same as
  // `sumApprovalRoundsPaise`'s CAS use above). This is the BASELINE the
  // server compares a decrease against.
  //
  // `projectedTotalPaise` mirrors what the server will actually compute
  // after this save. Orchestrator ruling 2026-09-19 (D1 fix, superseding the
  // FIX-10 draft that stripped the legacy row from the projection): the
  // legacy virtual row is now MATERIALISED into an ordinary round on the
  // first write (`buildApprovalRoundsPatchBody`, "history is not lost")
  // rather than dropped, so it counts toward the sent Σ exactly like any
  // other row still in `rounds` — projecting from the full rendered list is
  // correct again. The only way the legacy amount drops out of the sent Σ
  // is an EXPLICIT removal (the legacy row's own Remove control), which
  // already takes it out of `rounds` before this calculation runs.
  const draftAmountPaise = draft.amount === '' ? 0 : Math.round((Number(draft.amount) || 0) * 100);
  const currentTotalPaise = sumApprovalRoundsPaise(rounds);
  const projectedTotalPaise = !formOpen
    ? currentTotalPaise
    : editingId
      ? rounds.reduce((sum, r) => sum + (r.round_id === editingId ? draftAmountPaise : (Number(r.amount_paise) || 0)), 0)
      : currentTotalPaise + draftAmountPaise;
  const isDecrease = formOpen && draftAmountPaise > 0 && projectedTotalPaise < currentTotalPaise;
  const isOverApproved =
    formOpen && draftAmountPaise > 0 && billTotalPaise != null && projectedTotalPaise > billTotalPaise;

  // A removal ALWAYS lowers the total. The backend requires a reason only
  // once the write mints a settlement delta — i.e. inside the discharge flow
  // (DISCHARGE_INITIATED/BILLING_CLEARANCE, C3 `REASON_REQUIRED`).
  // Before that a removal stays a plain correction: no reason asked, and the
  // PATCH body is byte-identical to what FE-1 shipped.
  const deleteNeedsReason = isInDischargeFlow(admission && admission.status);

  useEffect(() => {
    if (!isDecrease && draft.reason) {
      setDraft((d) => ({ ...d, reason: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDecrease]);

  const openAdd = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (round) => {
    setEditingId(round.round_id);
    setDraft(draftFromRound(round));
    setError(null);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };
  const setField = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));

  /** One save path for add/edit/remove — always a full-list replace + CAS. */
  const save = async (nextRounds, removalReason) => {
    const expected = sumApprovalRoundsPaise(rounds);
    // The legacy virtual row (C1's read-time synthesis) never carries its
    // own `approved_on` — when it's materialised into a real round on this
    // write, fall back to the admission's admitted-on date (org-local,
    // via the SAME `todayYmd` the rest of the form defaults from — never a
    // hand-rolled UTC slice, see the IST day-boundary lesson).
    const legacyFallbackApprovedOn = admission && admission.admitted_at
      ? todayYmd(new Date(admission.admitted_at))
      : todayYmd();
    const body = buildApprovalRoundsPatchBody(nextRounds, expected, removalReason, legacyFallbackApprovedOn);
    setBusy(true);
    setError(null);
    const res = await patchAdmissionInsurance(admissionId, body);
    setBusy(false);
    if (res.success) {
      closeForm();
      setError(null);
      if (typeof onSaved === 'function') onSaved();
      return;
    }
    if (res.code === APPROVAL_ROUND_ERROR.APPROVAL_CHANGED) {
      // Never a silent overwrite — discard the stale edit, pull the real
      // rows via the parent's refresh, and tell the desk to look again.
      closeForm();
      setError('Approvals changed elsewhere — refreshed. Check the rows and try again.');
      if (typeof onSaved === 'function') onSaved();
      return;
    }
    if (res.code === APPROVAL_ROUND_ERROR.REASON_REQUIRED) {
      // Reachable only on a race (the form and the delete confirm both ask
      // for the reason first). Plain copy — never the wire message.
      setError('A reason is required when the approved total goes down. Add one and try again.');
      return;
    }
    setError(res.error || 'Failed to save the approval.');
  };

  const handleFormSave = () => {
    const amountPaise = Math.round((Number(draft.amount) || 0) * 100);
    if (!draft.approved_on) {
      setError('Date is required.');
      return;
    }
    if (!draft.amount || amountPaise <= 0) {
      setError('Amount is required.');
      return;
    }
    if (isDecrease && !draft.reason.trim()) {
      setError('A reason is required when lowering the approved amount.');
      return;
    }
    const realRoundCount = rounds.filter((r) => !r.isLegacy).length;
    if (!editingId && realRoundCount >= APPROVAL_ROUNDS_MAX) {
      setError(`No more than ${APPROVAL_ROUNDS_MAX} approval rounds are allowed.`);
      return;
    }
    const roundFields = {
      approved_on: draft.approved_on,
      amount_paise: amountPaise,
      reference: draft.reference,
      note: draft.note,
      reason: isDecrease ? draft.reason : '',
    };
    const next = editingId
      ? rounds.map((r) => (r.round_id === editingId ? { ...r, ...roundFields } : r))
      : [...rounds, roundFields];
    save(next);
  };

  const openDelete = (round) => {
    setDeleteReason('');
    setDeleteTarget(round);
  };

  const confirmDelete = () => {
    const target = deleteTarget;
    const reason = deleteReason.trim();
    setDeleteTarget(null);
    setDeleteReason('');
    save(rounds.filter((r) => r.round_id !== target.round_id), deleteNeedsReason ? reason : '');
  };

  // Money withheld: the server sends `approval_rounds: []`, which must not
  // read as "No approval recorded yet" under an empty header — render nothing.
  if (!admission || isMoneyHidden(admission)) return null;

  return (
    <div data-testid="approval-rounds-list">
      {canEdit && isInDischargeFlow(admission.status) && (
        <div className={bannerCls} data-testid="ar-discharge-banner">
          <span aria-hidden="true">⚠️</span>
          <span>Approval changes after discharge is initiated adjust the insurer settlement on the bill.</span>
        </div>
      )}

      {!canEdit && isTerminal(admission.status) && (
        <div className={lockedNoteCls} data-testid="ar-locked-note">
          Approval rounds are locked — the admission is complete.
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            className={addBtnCls}
            onClick={openAdd}
            disabled={formOpen}
            data-testid="ar-add-btn"
          >
            + Add approval
          </button>
        </div>
      )}

      {formOpen && (
        <div ref={formRef} className={formCls} data-testid="ar-form">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="ar-form-date">Date</label>
              <input
                id="ar-form-date"
                type="date"
                className={inputCls}
                value={draft.approved_on}
                max={todayYmd()}
                onChange={setField('approved_on')}
                data-testid="ar-form-date"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="ar-form-amount">Amount (₹)</label>
              <input
                id="ar-form-amount"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 5000"
                className={inputCls}
                value={draft.amount}
                onChange={setField('amount')}
                data-testid="ar-form-amount"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="ar-form-reference">Reference (optional)</label>
              <input
                id="ar-form-reference"
                type="text"
                maxLength={64}
                placeholder="Sanction letter no."
                className={inputCls}
                value={draft.reference}
                onChange={setField('reference')}
                data-testid="ar-form-reference"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="ar-form-note">Note (optional)</label>
              <input
                id="ar-form-note"
                type="text"
                maxLength={240}
                placeholder="e.g. Enhancement request"
                className={inputCls}
                value={draft.note}
                onChange={setField('note')}
                data-testid="ar-form-note"
              />
            </div>
            {isDecrease && (
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="ar-form-reason">Reason for the lower approval</label>
                <input
                  id="ar-form-reason"
                  type="text"
                  maxLength={240}
                  placeholder="e.g. Insurer revised the sanction down"
                  className={inputCls}
                  value={draft.reason}
                  onChange={setField('reason')}
                  data-testid="ar-form-reason"
                />
              </div>
            )}
          </div>
          {isOverApproved && (
            <div className={warnCls} data-testid="ar-over-approved-warning">
              Approved amount is more than the current bill total.
            </div>
          )}
          {error && <div className={errorCls} data-testid="ar-error">{error}</div>}
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" className={formBtn} onClick={closeForm} disabled={busy} data-testid="ar-form-cancel">
              Cancel
            </button>
            <button
              type="button"
              className={formBtnPrimary}
              onClick={handleFormSave}
              disabled={busy || (isDecrease && !draft.reason.trim())}
              data-testid="ar-form-save"
            >
              {busy ? 'Saving…' : 'Save approval'}
            </button>
          </div>
        </div>
      )}

      {!formOpen && error && <div className={errorCls} data-testid="ar-error">{error}</div>}

      {rounds.length === 0 ? (
        <div className="text-[12.5px] text-slate-500 dark:text-slate-400 py-1" data-testid="ar-empty">
          No approval recorded yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2 max-h-[430px] overflow-y-auto pr-0.5" data-testid="ar-rows">
          {rounds.map((r) => (
            <RoundRow
              key={r.round_id}
              round={r}
              canEdit={canEdit}
              onEdit={() => openEdit(r)}
              onRemove={() => openDelete(r)}
            />
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Remove this approval?"
        message={
          deleteTarget
            ? (
              <>
                <span>
                  {`Remove the ${formatPaise(deleteTarget.amount_paise)} approval${
                    deleteTarget.approved_on ? ` recorded on ${formatServiceDate(deleteTarget.approved_on)}` : ''
                  }? This cannot be undone.`}
                </span>
                {/* Reconcile item (a): removing a round after discharge is
                    initiated LOWERS the approved total, so the server takes
                    it as a decrease and requires a reason. Asked here, in
                    the confirm, rather than failing the click with a 422. */}
                {deleteNeedsReason && (
                  <>
                    <span className="block mt-2.5 text-[11px] font-semibold uppercase tracking-wide">
                      Reason for the lower approval
                    </span>
                    <input
                      type="text"
                      maxLength={240}
                      autoFocus
                      placeholder="e.g. Sanction letter withdrawn by the insurer"
                      className={`${inputCls} mt-1`}
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      data-testid="ar-delete-reason"
                    />
                  </>
                )}
              </>
            )
            : ''
        }
        ctaLabel="Remove"
        variant="danger"
        ctaDisabled={deleteNeedsReason && !deleteReason.trim()}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteReason(''); }}
      />
    </div>
  );
}

function RoundRow({ round, canEdit, onEdit, onRemove }) {
  if (round.isLegacy) {
    // "Read-only" means its fields (amount/date) can't be tweaked in place —
    // there is no Edit control, and never will be while it renders as this
    // virtual row. It CAN still be removed (orchestrator ruling 2026-09-19):
    // if the desk explicitly clears the old pre-rounds amount rather than
    // carrying it forward as round one, that's a real, ordinary removal —
    // same ConfirmationModal + reason-after-initiation path as any round.
    return (
      <div className={rowClsLegacy} data-testid={`ar-row-${round.round_id}`}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-2">
            <span className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
              Approval (recorded before rounds)
            </span>
            <span className="text-[14px] font-extrabold text-teal-700 dark:text-teal-300 tabular-nums">
              {formatPaise(round.amount_paise)}
            </span>
            <span className={readonlyChip} data-testid="ar-legacy-chip">Read-only</span>
          </div>
          {canEdit && (
            <div className="flex gap-1.5 flex-none">
              <button
                type="button"
                className={miniBtnDanger}
                onClick={onRemove}
                data-testid={`ar-remove-${round.round_id}`}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={rowCls} data-testid={`ar-row-${round.round_id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
            {formatServiceDate(round.approved_on)}
          </span>
          <span className="text-[14px] font-extrabold text-teal-700 dark:text-teal-300 tabular-nums">
            {formatPaise(round.amount_paise)}
          </span>
          {round.reference && (
            <span className="text-[11.5px] text-slate-500 dark:text-slate-400">Ref: {round.reference}</span>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1.5 flex-none">
            <button type="button" className={miniBtn} onClick={onEdit} data-testid={`ar-edit-${round.round_id}`}>
              Edit
            </button>
            <button type="button" className={miniBtnDanger} onClick={onRemove} data-testid={`ar-remove-${round.round_id}`}>
              Remove
            </button>
          </div>
        )}
      </div>
      {round.note && (
        <div className="text-[11.5px] italic text-slate-500 dark:text-slate-400 mt-1">&ldquo;{round.note}&rdquo;</div>
      )}
      {round.recorded_by && (
        <div className="text-[10.5px] text-slate-400 dark:text-slate-500 mt-1">
          Recorded by {round.recorded_by.name}
          {round.recorded_at ? ` · ${formatDateTimeIST(round.recorded_at)}` : ''}
        </div>
      )}
    </div>
  );
}
